import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaCalendarAlt,
    FaClock,
    FaCog,
    FaFileAlt,
    FaSearch,
    FaSpinner,
    FaUser
} from "react-icons/fa";
import { FaCheckToSlot } from "react-icons/fa6";
import {
    AuditItem,
    AuditObjectTypes,
    GetActionTypeString
} from "../../_core/_model.ts";
import {
    GetObjectAudits
} from "../../_core/_request.ts";
import AuditCard from "./AuditCard.tsx";
import Offcanvas from "react-bootstrap/esm/Offcanvas";
import moment from "moment";

const icons = {
    User: <FaUser className="text-primary" />,
    Settings: <FaCog className="text-secondary" />,
    Report: <FaFileAlt className="text-success" />,
    Default: <FaClock className="text-muted-theme" />,
};

interface request {
    asDrawer: boolean,
    instanceId: number;
    objectName: AuditObjectTypes;
    showAudit: boolean;
    setShowAudit: any;
    auditObjectName: string
}

const datePresets = [
    { label: "All", range: () => ({ start: null, end: null }) },
    {
        label: "Today", range: () => {
            const today = new Date();
            return {
                start: new Date(today.setHours(0, 0, 0, 0)),
                end: new Date(today.setHours(23, 59, 59, 999)),
            };
        }
    },
    {
        label: "Last 7 Days", range: () => {
            const today = new Date();
            const past = new Date(today);
            past.setDate(today.getDate() - 6);
            return { start: past, end: today };
        }
    },
    {
        label: "Last Month", range: () => {
            const now = new Date();
            const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const last = new Date(now.getFullYear(), now.getMonth(), 0);
            return { start: first, end: last };
        }
    },
    { label: "Custom", range: () => ({ start: null, end: null }) },
];

const AuditList = ({ asDrawer, instanceId, objectName, auditObjectName, showAudit, setShowAudit }: request) => {
    const [audits, setAudits] = useState<AuditItem[]>();
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedPreset, setSelectedPreset] = useState("All");
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [pageSize, setPageSize] = useState<number>(50);

    useEffect(() => {
        GetObjectAudits(objectName, instanceId, pageSize)
            .then((x) => setAudits([...x.data]))
            .catch(() => setAudits([]));
    }, [objectName, instanceId, pageSize, showAudit]);

    useEffect(() => {
        const preset = datePresets.find((p) => p.label === selectedPreset);
        if (preset && selectedPreset !== "Custom") {
            const { start, end } = preset.range();
            setStartDate(start);
            setEndDate(end);
        }
    }, [selectedPreset]);

    const getName = (item: AuditItem) => {
        return item.forInstanceName || "Unnamed";
    };

    const filtered = audits?.filter((a) => {
        const matchesText = !search ||
            a.forInstanceName?.toLowerCase().includes(search.toLowerCase()) ||
            GetActionTypeString(a.actionType).toLowerCase().includes(search.toLowerCase());

        const auditDate = moment.tz(a.onDate, 'America/Los_Angeles').local().toDate();
        const matchesStart = !startDate || auditDate >= startDate;
        const matchesEnd = !endDate || auditDate <= endDate;

        return matchesText && matchesStart && matchesEnd;
    });

    const auditBody = () => {
        return (
            typeof audits === "undefined" ? (
                <div className="d-flex flex-column align-items-center text-success mt-4">
                    <AnimatePresence>
                        <motion.div
                            animate={{ rotate: 360 }}
                            style={{
                                display: 'inline-block',
                                width: '40px',
                                height: '40px',
                                marginBottom: '10px'
                            }}
                            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                        >
                            <FaSpinner className="fa-spin mb-2" size={32} style={{
                                fontSize: '2.5rem',
                                color: '#198754',
                                width: '100%',
                                height: '100%'
                            }} />
                        </motion.div>
                    </AnimatePresence>
                    <span>Loading Audits...</span>
                </div>
            ) : filtered?.length === 0 ? (
                <motion.div
                    className="d-flex flex-column align-items-center justify-content-center text-muted-theme px-5 mx-4 my-3"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    style={{
                        border: "2px dashed #dee2e6",
                        borderRadius: "10px",
                        backgroundColor: "#f9f9f9",
                        marginBottom: "10px",
                    }}
                >
                    <FaCheckToSlot size={60} color="#adb5bd" className="mb-3" />
                    <h5 className="fw-semibold">No Audit Found</h5>
                    <p className="text-center" style={{ maxWidth: 400 }}>
                        No audit entries match your search.
                    </p>
                </motion.div>
            ) : (
                <div className="py-3 ps-3 pe-2">
                    {filtered?.map((audit, idx) => (
                        <div key={idx} className="mb-3">
                            <AuditCard audit={audit} name={getName(audit)} />
                        </div>
                    ))}
                </div>
            )
        )
    }

    return (
        asDrawer ?
            <Offcanvas show={showAudit} className="card" onHide={() => setShowAudit(false)} placement="end" style={{ width: '600px' }}>
                <Offcanvas.Header closeButton className="audit-header border-bottom flex-column w-100 pb-0 align-items-start justify-content-start pb-3">
                    <Offcanvas.Title>Audit for {auditObjectName}</Offcanvas.Title>
                    <div
                        className="pb-0 pt-3 w-100 px-0">
                        <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2">
                            {/* Search Box and Calendar Toggle */}
                            <div className="flex-grow-1">
                                <div className="input-group">
                                    <span className="input-group-text text-muted-theme bg-theme-card">
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control search-control-with-button"
                                        placeholder="Search by name or action..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className={`btn ${showDateFilter ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                                        title={showDateFilter ? "Hide date filters" : "Show date filters"}
                                        onClick={() => setShowDateFilter(!showDateFilter)}
                                    >
                                        <FaCalendarAlt className="me-1" />
                                        <span className="d-none d-sm-inline">
                                            {showDateFilter ? "Hide" : "Show"}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Page Size Dropdown */}
                            <div className="d-flex align-items-center ms-md-3 mt-2 mt-md-0 pe-1">
                                <span className="me-2 text-muted-theme small">Show</span>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: "70px", height: "33px" }}
                                    value={pageSize}
                                    onChange={(e) => setPageSize(parseInt(e.target.value))}
                                >
                                    {[50, 100, 500, 1000].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Date Filter Presets */}
                    <AnimatePresence initial={false}>
                        {showDateFilter && (
                            <motion.div
                                className="row g-2 align-items-end w-100 px-2 mt-1"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="col-md-3 col-sm-6">
                                    <label className="form-label">Date Range</label>
                                    <select
                                        className="form-select"
                                        value={selectedPreset}
                                        onChange={(e) => setSelectedPreset(e.target.value)}
                                    >
                                        {datePresets.map((preset) => (
                                            <option key={preset.label} value={preset.label}>
                                                {preset.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedPreset === "Custom" && (
                                    <>
                                        <div className="col-md-3 col-sm-6">
                                            <label className="form-label">Start Date</label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                className="form-control"
                                                placeholderText="Start Date"
                                                isClearable
                                            />
                                        </div>
                                        <div className="col-md-3 col-sm-6">
                                            <label className="form-label">End Date</label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={startDate || undefined}
                                                className="form-control"
                                                placeholderText="End Date"
                                                isClearable
                                            />
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Offcanvas.Header>
                <Offcanvas.Body style={{ padding: "0px" }}>
                    {
                        auditBody()
                    }
                </Offcanvas.Body>
            </Offcanvas> :
            auditBody()
    );
};

export default AuditList;
